import { ThunkDispatch } from "core-integration/src/store";
import { getElementStatsByIdentifier } from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { IElementsById } from "core-integration/src/store/elements/types";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getGroupedFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import {
  IFixtureStat,
  IFixtureStats,
} from "core-integration/src/store/fixtures/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { format } from "date-fns";
import { size } from "polished";
import * as React from "react";
import { useEffect, useState } from "react";
import {
  Trans,
  useTranslation,
  WithTranslation,
  withTranslation,
} from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { dateLocales } from "../i18n";
import ArrowUp from "../img/icons/arrow-up.svg?react";
import { getStatDetails } from "../utils/stats";
import Button from "./Button";
import EcalButton from "./EcalButton";
import Fixture from "./Fixture";
import { Pager, PagerButton, PagerButtonNext } from "./Pager";
import { ArrowLeft, ArrowRight } from "./icons/Arrows";

const NoteWrapper = styled.div`
  padding: ${({ theme }) => theme.space[2]};
`;

const FixturesGroupWrapper = styled.div`
  margin: ${({ theme }) => `0 ${theme.space[1]} ${theme.space[4]}`};
`;

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space[3]};
`;

const StyledArrowRight = styled(ArrowRight)`
  ${size(15, "auto")};
`;
const StyledArrowLeft = styled(ArrowLeft)`
  ${size(15, "auto")};
`;

const Deadline = styled.h2`
  margin: ${({ theme }) => theme.space[2]};
  font-size: ${({ theme }) => theme.fontSizes[3]};
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.impact};
  text-transform: uppercase;
  font-weight: 600;
`;

const Heading = styled.h3`
  margin: ${({ theme }) => theme.space[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: 2.4rem;
  font-family: ${({ theme }) => theme.fonts.impact};
`;

const Note = styled.p`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
`;

const FixtureDay = styled.h4`
  margin: 0;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[2]}`};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  text-align: center;
`;

const FixtureTime = styled.time`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.white};
`;

const FixtureList = styled.ul`
  margin: 0;
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureItem = styled.li`
  margin-bottom: ${({ theme }) => theme.space[2]};
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureDisclosureWrapper = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const FixtureButton = styled.button<IStyledFixtureStatsProps>`
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  text-align: start;
  background-color: transparent;
  cursor: pointer;

  ${(props) =>
    props.isOpen &&
    css`
      border-left: ${({ theme }) => theme.borders[2]};
      border-top: ${({ theme }) => theme.borders[2]};
      border-right: ${({ theme }) => theme.borders[2]};
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      background: ${(props) => props.theme.colors.white4};
    `}
`;

const ArrowButton = styled.button<IStyledFixtureStatsProps>`
  position: absolute;
  top: 50%;
  inset-inline-end: ${({ theme }) => theme.space[2]};
  transform: translateY(-50%);
  display: flex;
  padding: ${({ theme }) => theme.space[1]};
  border: none;
  background: transparent;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    transform: rotate(180deg);
    transition: transform 0.2s;

    path {
      fill: #ffffff;
    }

    ${(props) =>
      props.isOpen &&
      css`
        transform: rotate(0deg);
      `}
  }
`;

const FixtureStatsWrap = styled.div<IStyledFixtureStatsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  border-end-start-radius: ${({ theme }) => theme.radii[2]};
  border-left: ${({ theme }) => theme.borders[2]};
  border-right: ${({ theme }) => theme.borders[2]};
  border-bottom: ${({ theme }) => theme.borders[2]};
  border-end-end-radius: ${({ theme }) => theme.radii[2]};
  padding: ${({ theme }) => `${theme.space[2]} 5%`};
  background-color: ${({ theme }) => theme.colors.white4};

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    padding-inline-end: 15%;
    padding-inline-start: 15%;
  }
`;

const FixtureStatsOuterList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FixtureStat = styled.li`
  margin-bottom: ${({ theme }) => theme.space[4]};
  border: ${({ theme }) => theme.borders[2]};
  border-end-start-radius: ${({ theme }) => theme.radii[1]};
  border-end-end-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.white4};
  color: ${({ theme }) => theme.colors.white1};
`;

const FixtureStatHeading = styled.h5`
  margin: 0;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[2]}`};
  border-end-start-radius: ${({ theme }) => theme.radii[0]};
  border-end-end-radius: ${({ theme }) => theme.radii[0]};
  background-color: ${({ theme }) => theme.colors.grey24};
  font-size: ${({ theme }) => theme.fontSizes[0]};
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
`;

const FixtureStatBody = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.space[2]};
  padding-bottom: ${({ theme }) => theme.space[2]};
  border-end-start-radius: ${({ theme }) => theme.radii[1]};
  border-end-end-radius: ${({ theme }) => theme.radii[1]};
`;

const FixtureStatList = styled.ul`
  flex: 1;
  padding-inline-end: ${({ theme }) => theme.space[2]};
  padding-inline-start: ${({ theme }) => theme.space[2]};

  &:first-child {
    border-inline-end: ${({ theme }) => theme.borders[3]};
    text-align: end;
  }
`;

const FixtureStatItem = styled.li`
  padding-top: ${({ theme }) => theme.space[1]};
  padding-bottom: ${({ theme }) => theme.space[1]};
  border-bottom: ${({ theme }) => theme.borders[2]};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  list-style: none;
`;

const ElementButton = styled.button`
  background-color: transparent;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.colors.white1};
  font-size: ${({ theme }) => theme.fontSizes[2]};

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const StyledSection = styled.section`
  border: 0.5px solid ${({ theme }) => theme.colors.white16};
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors.white4};

  /* Remove border-bottom from the very last fixture */
  > div:last-child li:last-child > div {
    border-bottom: none;
  }
`;

interface IFixtureStatsForTeamProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStat[];
}

const FixtureStatsForTeam = ({
  elementsById,
  showElementDialog,
  stats,
}: IFixtureStatsForTeamProps) => (
  <FixtureStatList>
    {stats
      .filter((fs) => elementsById[fs.element])
      .map((fs) => (
        <FixtureStatItem key={fs.element}>
          <ElementButton
            onClick={(_: React.MouseEvent<HTMLButtonElement>) =>
              showElementDialog(fs.element)
            }
          >
            {elementsById[fs.element].web_name}
          </ElementButton>{" "}
          ({fs.value})
        </FixtureStatItem>
      ))}
  </FixtureStatList>
);

interface IFixtureStatsProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStats[];
  statsByIdentifier: Record<string, IElementStat>;
}

interface IStyledFixtureStatsProps {
  isOpen?: boolean;
}

type FixtureStatProps = IFixtureStatsProps & IStyledFixtureStatsProps;

const FixtureStats = ({
  elementsById,
  isOpen = false,
  showElementDialog,
  stats,
}: FixtureStatProps) => {
  const { t } = useTranslation();
  // Our render stats should contain only stats with data and a maximum
  // of 5 per team of BPS
  const renderStats = stats
    .filter((stat) => stat.h.length || stat.a.length)
    .map((stat) =>
      stat.identifier === "bps"
        ? {
            identifier: stat.identifier,
            h: stat.h.slice(0, 5),
            a: stat.a.slice(0, 5),
          }
        : stat,
    );
  return (
    <FixtureStatsWrap isOpen={isOpen}>
      <FixtureStatsOuterList>
        {renderStats.map((stat) => (
          <FixtureStat key={stat.identifier}>
            <FixtureStatHeading>
              {getStatDetails(stat.identifier, t)!.label}
            </FixtureStatHeading>
            <FixtureStatBody>
              <FixtureStatsForTeam
                stats={stat.h}
                elementsById={elementsById}
                showElementDialog={showElementDialog}
              />
              <FixtureStatsForTeam
                stats={stat.a}
                elementsById={elementsById}
                showElementDialog={showElementDialog}
              />
            </FixtureStatBody>
          </FixtureStat>
        ))}
      </FixtureStatsOuterList>
    </FixtureStatsWrap>
  );
};

interface IOwnProps {
  eventId?: number;
  hidePager?: boolean;
  useLinks?: boolean;
}

type Props = IOwnProps & WithTranslation;
declare global {
  interface Window {
    postscribe: any;
  }
}

const Fixtures = ({
  eventId: initialEventId,
  hidePager = false,
  i18n,
  t,
  useLinks,
}: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const currentEvent = useSelector(getCurrentEvent);
  const elementsById = useSelector(getElementsById);
  const eventsById = useSelector(getEventsById);
  const groupedFixturesByEvent = useSelector(getGroupedFixturesByEvent);
  const nextEvent = useSelector(getNextEvent);
  const statsByIdentifier = useSelector(getElementStatsByIdentifier);
  const teamsById = useSelector(getTeamsById);

  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
  const [eventId, setEventId] = useState(() => {
    if (initialEventId) {
      return initialEventId;
    } else if (nextEvent) {
      return nextEvent.id;
    } else if (currentEvent) {
      return currentEvent.id;
    }
    return 0;
  });

  useEffect(() => {
    dispatch(fetchFixtures(eventId));
  }, [dispatch, eventId]);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const changeEvent = (newEventId: number) => {
    setEventId(newEventId);
    dispatch(fetchFixtures(newEventId));
  };

  const handlePreviousClick = () => changeEvent(eventId - 1);
  const handleNextClick = () => changeEvent(eventId + 1);

  const toggle = (fixtureId: number) => {
    setIsOpen((prevState) => ({
      ...prevState,
      [fixtureId]: !prevState[fixtureId],
    }));
  };

  const event = eventsById[eventId];
  if (!event) {
    return null;
  }

  // Button state logic - disable if no previous/next event exists
  const previousEnabled = Boolean(eventsById[eventId - 1]);
  const nextEnabled = Boolean(eventsById[eventId + 1]);

  const nextClickHandler = nextEnabled ? handleNextClick : undefined;
  const previousClickHandler = previousEnabled
    ? handlePreviousClick
    : undefined;

  const eventFixtureGroups = groupedFixturesByEvent[eventId] || [];
  return (
    <StyledSection>
      <header>
        <Deadline>
          {event.name} -{" "}
          <time dateTime={formatRawAsISO(event.deadline_time)}>
            {formatRawAsLocalI18n(
              event.deadline_time,
              dateLocales[i18n.language],
            )}
          </time>
        </Deadline>
        <Heading>
          <span>{t("fixtures.title", "Fixtures")}</span>
        </Heading>
        <ButtonWrap>
          <EcalButton variant="styled" gtmClass="button">
            <span>
              {t("fixtures.syncToCalendar", "Add fixtures to your calendar")}
            </span>
          </EcalButton>
        </ButtonWrap>
        {!hidePager && (
          <div>
            <Pager>
              <PagerButton>
                {useLinks ? (
                  <Button
                    width={1}
                    to={
                      previousEnabled ? `/fixtures/${eventId - 1}` : undefined
                    }
                    onClick={previousClickHandler}
                    disabled={!previousEnabled}
                    variant="tertiary"
                    textTransform="upperCase"
                    justifyContent="spaceBetween"
                    startIcon={<StyledArrowLeft />}
                  >
                    {t("fixtures.previous", "Previous")}
                  </Button>
                ) : (
                  <Button
                    width={1}
                    onClick={previousClickHandler}
                    disabled={!previousEnabled}
                    variant="tertiary"
                    justifyContent="spaceBetween"
                    textTransform="upperCase"
                    startIcon={<StyledArrowLeft />}
                  >
                    {t("fixtures.previous", "Previous")}
                  </Button>
                )}
              </PagerButton>

              <PagerButtonNext>
                {useLinks ? (
                  <Button
                    width={1}
                    to={nextEnabled ? `/fixtures/${eventId + 1}` : undefined}
                    onClick={nextClickHandler}
                    disabled={!nextEnabled}
                    variant="tertiary"
                    textTransform="upperCase"
                    justifyContent="spaceBetween"
                    endIcon={<StyledArrowRight />}
                  >
                    {t("fixtures.next", "Next")}{" "}
                  </Button>
                ) : (
                  <Button
                    width={1}
                    onClick={nextClickHandler}
                    disabled={!nextEnabled}
                    variant="tertiary"
                    textTransform="upperCase"
                    justifyContent="spaceBetween"
                    endIcon={<StyledArrowRight />}
                  >
                    {t("fixtures.next", "Next")}
                  </Button>
                )}
              </PagerButtonNext>
            </Pager>
          </div>
        )}
      </header>
      <NoteWrapper>
        <Note>
          <Trans i18nKey="fixtures.localTimes">
            All times are shown in your <strong>local time</strong>
          </Trans>
        </Note>
      </NoteWrapper>
      {eventFixtureGroups.map((group) => (
        <FixturesGroupWrapper key={group.date.toISOString()}>
          <FixtureDay>
            <FixtureTime dateTime={group.date.toISOString()}>
              {format(group.date, "EEEE d MMMM yyyy")}
            </FixtureTime>
          </FixtureDay>
          <FixtureList>
            {group.fixtures.map((f) => (
              <React.Fragment key={f.id}>
                {f.started && f.team_h_score !== null ? (
                  <FixtureItem>
                    <FixtureDisclosureWrapper>
                      <FixtureButton
                        onClick={() => toggle(f.id)}
                        isOpen={isOpen[f.id]}
                        aria-expanded={isOpen[f.id]}
                        aria-label={`${teamsById[f.team_h].name} ${
                          f.team_h_score
                        } ${teamsById[f.team_a].name} ${f.team_a_score}`}
                      >
                        <Fixture fixture={f} />
                      </FixtureButton>
                      <ArrowButton
                        isOpen={isOpen[f.id]}
                        onClick={() => toggle(f.id)}
                        aria-expanded={isOpen[f.id]}
                      >
                        <ArrowUp />
                      </ArrowButton>
                    </FixtureDisclosureWrapper>
                    <FixtureStats
                      stats={f.stats}
                      elementsById={elementsById}
                      showElementDialog={showElementDialog}
                      statsByIdentifier={statsByIdentifier}
                      isOpen={isOpen[f.id]}
                    />
                  </FixtureItem>
                ) : (
                  <FixtureItem>
                    <Fixture fixture={f} />
                  </FixtureItem>
                )}
              </React.Fragment>
            ))}
          </FixtureList>
        </FixturesGroupWrapper>
      ))}
    </StyledSection>
  );
};

export default withTranslation()(Fixtures);
